/**
 * Modal de cierre de compensación — Sprint 17 Fase 2.
 *
 * Caso de uso: vecino que compensaba un servicio se va del barrio.
 * Setea activa=false + registra motivo + cerrado_at + vigente_hasta
 * (default: hoy). Aplicaciones previas no se afectan; ya no se puede
 * aplicar más.
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { cerrarCompensacion } from '../../lib/api/compensaciones';
import type { CompensacionConDetalle } from '../../lib/api/types';
import { Button } from '../ui/Button';
import { InputFecha } from '../ui/InputFecha';
import { hoy } from '../../lib/tiempo';

interface Props {
  compensacion: CompensacionConDetalle;
  onCerrar: () => void;
  onCerrada: () => void;
}

function hoyISO(): string {
  return hoy();
}

export function ModalCerrarCompensacion({
  compensacion,
  onCerrar,
  onCerrada,
}: Props) {
  const [motivo, setMotivo] = useState('');
  const [vigenteHasta, setVigenteHasta] = useState(hoyISO());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const motivoValido = motivo.trim().length >= 3;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!motivoValido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await cerrarCompensacion(compensacion.id, {
        motivo_cierre: motivo.trim(),
        vigente_hasta: vigenteHasta || null,
      });
      onCerrada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Cerrar compensación"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Cerrar compensación
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="text-sm text-text-secondary border-l-2 border-state-warning/50 pl-3">
          {compensacion.descripcion} · Casa {compensacion.lote_identificador} ·{' '}
          {compensacion.vecino_nombre}
        </div>

        <div className="bg-state-warning/[0.08] border-l-4 border-state-warning p-3 rounded-sm text-xs text-text-secondary">
          La compensación quedará marcada como cerrada. Las{' '}
          <strong>{compensacion.n_aplicaciones} aplicaciones previas</strong>{' '}
          se mantienen intactas — los gastos y pagos virtuales históricos no
          se tocan. A partir de la fecha que indiques, ya no se podrá
          aplicar más.
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Motivo del cierre <span className="text-state-danger">*</span>
          </span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            placeholder="Ej: Vecino se retira del barrio el 2026-06-30."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
            minLength={3}
            maxLength={1000}
          />
          <span className="text-xs text-text-muted">
            Quedará registrado en la card de la compensación cerrada.
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Vigente hasta (último día válido)
          </span>
          <InputFecha
            value={vigenteHasta}
            onChange={setVigenteHasta}
            min={compensacion.vigente_desde}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!motivoValido || submitting}
          >
            {submitting ? 'Cerrando…' : 'Cerrar compensación'}
          </Button>
        </div>
      </form>
    </div>
  );
}
